import lightGallery from "lightgallery";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgZoom from 'lightgallery/plugins/zoom';
import relativeCaption from "lightgallery/plugins/relativeCaption";

class Gallery {
    constructor(gallery) {
        this.prevOpenedSlide = null;
        this.license = '3A898F6F-AFDF-4656-9CA5-26BF7F639CA4';
        this.gallery = gallery;
        this.plugin = null;
        this.mode = null;
        this.maximizeClickHandler = null;
        this.slideLoadHandler = null;
        this.openTimeout = null;
        this.transitionToken = 0;
        this.teardownUntil = 0;
    }

    findTallestElement(parentElement) {
        if (!parentElement) {
            return;
        }

        let imgHeight = parentElement.querySelector('.lg-img-wrap');
        let captionHeight = parentElement.querySelector('.lg-sub-html');

        if (captionHeight) {
            captionHeight.classList.add('visible');
        }

        if (imgHeight && captionHeight && imgHeight.scrollHeight !== 0) {
            parentElement.style.maxHeight = imgHeight.scrollHeight + captionHeight.scrollHeight + 'px';
            captionHeight.classList.add('visible');
        }
      }

    initDesktopGallery() {
        this.plugin = lightGallery(this.gallery, {
            plugins: [lgThumbnail, relativeCaption, lgZoom],
            speed: 500,
            subHtmlSelectorRelative: true,
            appendSubHtmlTo: '.lg-item',
            addClass: Array.from(this.gallery.classList).join(' '),
            thumbMargin: 22,
            thumbWidth: 125,
            thumbHeight: '94px',
            download: false,
            counter: false,
            licenseKey: this.license,
        });

        this.slideLoadHandler = () => {
            this.findTallestElement(this.plugin.outer.selector.querySelector('.lg-inner .lg-current'));
        };
        this.gallery.addEventListener('lgSlideItemLoad', this.slideLoadHandler);
        this.mode = 'desktop';
    }

    initMobileGallery() {
        this.plugin = lightGallery(this.gallery, {
            container: this.gallery,
            plugins: [relativeCaption, lgThumbnail, lgZoom],
            addClass: Array.from(this.gallery.classList).join(' '),
            selectWithin: `#${this.gallery.getAttribute('id')}`,
            vimeoThumbSize: 'thumbnail_medium',
            subHtmlSelectorRelative: true,
            speed: 500,
            thumbMargin: 9,
            thumbWidth: 78,
            thumbHeight: '50px',
            closable: false,
            hash: false,
            download: false,
            licenseKey: this.license,
            showMaximizeIcon: true,
            mobileSettings: {
                controls: true,
            }
        });

        const maximize = this.gallery.querySelector('.lg-maximize');
        if (maximize) {
            this.maximizeClickHandler = () => {
                if (document.body.classList.contains('lg-open')) {
                    document.body.classList.remove('lg-open');
                } else {
                    document.body.classList.add('lg-open');
                }
            };
            maximize.addEventListener('click', this.maximizeClickHandler);
        }

        this.openTimeout = setTimeout(() => {
            this.openTimeout = null;
            if (this.plugin) {
                this.plugin.openGallery();
            }
        }, 200);
        this.mode = 'mobile';
    }

    teardown() {
        if (this.openTimeout) {
            clearTimeout(this.openTimeout);
            this.openTimeout = null;
        }
        if (this.slideLoadHandler) {
            this.gallery.removeEventListener('lgSlideItemLoad', this.slideLoadHandler);
            this.slideLoadHandler = null;
        }
        if (this.maximizeClickHandler) {
            const maximize = this.gallery.querySelector('.lg-maximize');
            if (maximize) {
                maximize.removeEventListener('click', this.maximizeClickHandler);
            }
            this.maximizeClickHandler = null;
        }

        let destroyDuration = 0;
        if (this.plugin) {
            try {
                destroyDuration = this.plugin.destroy() || 0;
            } catch (e) {
                destroyDuration = 0;
            }
            this.plugin = null;
        }
        document.body.classList.remove('lg-open');
        this.mode = null;
        this.teardownUntil = Math.max(this.teardownUntil, performance.now() + destroyDuration);
        const remaining = this.teardownUntil - performance.now();
        return remaining > 0 ? Math.ceil(remaining) + 1 : 0;
    }

    applyMode(isMobile) {
        const desiredMode = isMobile ? 'mobile' : 'desktop';
        if (this.mode === desiredMode) return;

        const wait = this.teardown();
        const token = ++this.transitionToken;

        const start = () => {
            if (token !== this.transitionToken) return;
            if (isMobile) {
                this.initMobileGallery();
            } else {
                this.initDesktopGallery();
            }
        };

        if (wait > 0) {
            setTimeout(start, wait);
        } else {
            start();
        }
    }

    // lightGallery only writes an alt attribute when the source image carries a non-empty one, so
    // a decorative image (alt="") produces a generated slide and thumbnail with no alt at all and
    // screen readers announce the file name. Copy each item's own alt across, empty included.
    syncGeneratedImageAlt() {
        if (!this.plugin) {
            return;
        }

        const scope = (this.plugin.outer && this.plugin.outer.selector) || this.gallery;
        const items = this.plugin.galleryItems || [];

        scope.querySelectorAll('img.lg-object, img.lg-dummy-img, .lg-thumb-item img').forEach((image) => {
            if (image.hasAttribute('alt')) {
                return;
            }

            // The index lives on the image for slides and on the image or its wrapper for
            // thumbnails. Without one, the image is decorative rather than item zero's.
            const owner = image.closest('[data-index], [data-lg-item-id]');
            const id = owner && (owner.getAttribute('data-index') || owner.getAttribute('data-lg-item-id'));
            const item = id ? items[Number(id)] : null;
            image.setAttribute('alt', (item && item.alt) || '');
        });
    }

    attachEvents() {
        this.gallery.addEventListener('keydown', (e) => {
            if (e.target.matches('a') && e.key === 'Enter') {
                e.target.click();
                this.prevOpenedSlide = e.target
            }
        })

        const generatedImageEvents = ['lgAfterAppendSlide', 'lgAfterOpen', 'lgAfterSlide'];
        generatedImageEvents.forEach((event) => {
            this.gallery.addEventListener(event, () => this.syncGeneratedImageAlt());
        });

        this.gallery.addEventListener('lgAfterClose', () => {
            if (!this.prevOpenedSlide) {
                return;
            }

            this.prevOpenedSlide.focus();
            this.prevOpenedSlide = null;
        });
    }

    init () {
        if (!this.gallery) {
            return;
        }

        this.attachEvents();

        const tablet = window.matchMedia("(max-width: 64rem)");
        this.applyMode(tablet.matches);
        tablet.addEventListener('change', (e) => this.applyMode(e.matches));
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.gallery-container').forEach((gallery) => {
        if (gallery.classList.contains('init')) {
            return;
        }
       new Gallery(gallery).init();
       gallery.classList.add('init');
    });
});
